import { useMutation, useQueryClient } from '@tanstack/vue-query';
import { toast } from 'vue3-toastify';

import formalizationApi from '~/services/formalization-api/formalization-api';
import { queryKeys } from '~/store/queryClient';
import { formatDocumentForDisplay } from '~/utils/formatters';

interface Variables {
  financingProjectId: string;
  newCustomerDocument: string;
  projectId: string;
}

const getErrorMessage = (error: any): string => {
  const status = error?.response?.status;
  const detail = error?.response?.data?.detail;

  if (status === 404) {
    return detail === 'Customer not found'
      ? 'Cliente com este documento não encontrado'
      : 'Financiamento não encontrado na cobrança';
  }

  const errorMessages = {
    400: 'Documento inválido ou igual ao do titular atual.',
    401: 'Sem permissão para trocar a titularidade.',
    403: 'Sem permissão para trocar a titularidade.',
  } as const;

  return errorMessages[status as keyof typeof errorMessages] || 'Erro inesperado ao trocar a titularidade.';
};

export default function useTitularityTransfer() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ financingProjectId, newCustomerDocument }: Variables) => formalizationApi.transferTitularity({
      newCustomerDocument,
      projectId: financingProjectId,
    }),
    onError: (error: any) => {
      toast.error(getErrorMessage(error));
    },
    onSuccess: (data, { projectId }) => {
      toast.success(
        `Titularidade trocada: ${formatDocumentForDisplay(data.old_customer_document)}`
        + ` → ${formatDocumentForDisplay(data.new_customer_document)}`,
      );
      queryClient.invalidateQueries({ queryKey: queryKeys.FINANCING.DETAILS(projectId) });
    },
    // the global mutation default retries every non-401 error forever, which hides 4xx feedback
    retry: false,
  });
}

export { getErrorMessage as getTitularityTransferErrorMessage };
